import { Container, EmptyAction, IntervalAction, Platform, T_Platform } from '@cgs/wge';
import { SpinResponse } from '@cgs/data-access';
import {
  APP_SETTINGS_SERVICE_TOKEN,
  AppSettingsService,
  AwaitableAction,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameStateMachineProvider,
} from '@cgs/machines';
import { TRIFECTA_SDK_ADAPTER_TOKEN, TrifectaSDKAdapter } from '../../sdk/trifecta-sdk.adapter';
import { TRIFECTA_GAME_HOOK_TOKEN, TrifectaGameHook } from './game-hook/trifecta-game-hook';
import { TRIFECTA_LAUNCH_PARAMS_TOKEN, TrifectaLaunchParamsDto } from '../../models/trifecta-dto';

export const HUD_CONTROLLER_TOKEN = Symbol('HudController');

export class TrifectaHudController {
  private readonly trifectaSDKAdapter: TrifectaSDKAdapter;
  private readonly launchParams: TrifectaLaunchParamsDto;
  private readonly trifectaGameHook: TrifectaGameHook;
  private readonly appSettings: AppSettingsService;
  private readonly platform: Platform;
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;

  constructor(container: Container) {
    this.trifectaSDKAdapter = container.resolve<TrifectaSDKAdapter>(TRIFECTA_SDK_ADAPTER_TOKEN);
    this.launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);
    this.trifectaGameHook = container.resolve<TrifectaGameHook>(TRIFECTA_GAME_HOOK_TOKEN);
    this.appSettings = container.resolve<AppSettingsService>(APP_SETTINGS_SERVICE_TOKEN);
    this.platform = container.resolve<Platform>(T_Platform);

    const gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine as GameStateMachine<SpinResponse>;
    this.gameStateMachine = gameStateMachine;

    gameStateMachine.init.addLazyAnimationToBegin(() => this.handleRecovery());
    // Sync sound at init.entered (fires before start page becomes interactive).
    // We do NOT call updateSound() immediately after SDK init because isSoundOn
    // may still be at its default true value before player prefs are loaded — which
    // would briefly enable sound even when the player has it off.
    void gameStateMachine.init.entered.first.then(() => this.updateSound());
    // Subscribe synchronously so no onSoundChange events are missed.
    this.trifectaSDKAdapter.onSoundChange(() => this.updateSound());

    void this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.trifectaSDKAdapter.initialize();
  }

  // TODO: то все костыли, после рефакторинга надо удались. Надо все в betsStore перевести
  // аналог this.slotSession.currentBet.bet
  // еще умножаем на Math.pow(10, this.launchParams.currencyDecimals) чтобы потом нормально работал numberFormatter
  public getCurrentBet(): number {
    return (
      (this.trifectaSDKAdapter.getBet() / this.launchParams.initData.maxLines) *
      Math.pow(10, this.launchParams.currencyDecimals)
    );
  }

  // Total bet of the current round in response units (same units as curResponse.totalWin).
  // Use this for big/mega/epic win counter thresholds instead of slotSession.totalBet.
  public getTotalBet(): number {
    // In replay mode the SDK bet holds the amount actually paid for the round
    // (for buy feature — nominal bet × buyFeatureMultiplier), so the round's
    // nominal bet must be taken from the response itself.
    if (this.launchParams.replayData) {
      const group = this.gameStateMachine.curResponse?.freeSpinsInfo?.currentFreeSpinsGroup;
      if (group?.bet && group.lines) {
        return group.bet * group.lines;
      }
    }
    return this.getCurrentBet() * this.launchParams.initData.maxLines;
  }

  public get isSkipIntro(): boolean {
    return this.trifectaSDKAdapter.skipIntro;
  }

  protected handleRecovery(): IntervalAction {
    if (!this.trifectaSDKAdapter.config.hasResumeData) {
      return new EmptyAction();
    }

    return new AwaitableAction(this.trifectaGameHook.gameResumed.toPromise());
  }

  protected updateSound(): void {
    const isSoundOn = this.trifectaSDKAdapter.isSoundOn;
    this.appSettings.sounds = isSoundOn ? 1 : 0;
    if (!isSoundOn) {
      // AudioSystem saves _lastVolume on pause() and restores it on resume().
      // Re-pausing after muting ensures _lastVolume = 0, so any subsequent
      // resume() call (e.g. from tab focus) won't un-mute.
      this.platform.audioSystem.pause();
    }
  }
}
